"use client";

import { type ReactNode, useState } from "react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
type ConfirmationDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  description: ReactNode;
  confirmText?: string;
  /** Confirm label while `onConfirm` (or `loading`) is pending, e.g. "Excluindo...". */
  pendingText?: string;
  cancelText?: string;
  variant?: "default" | "destructive";
  loading?: boolean;
  /** If true, parent handles closing the dialog after onConfirm */
  manualClose?: boolean;
};

/**
 * The single confirmation pattern for the app. Use it instead of composing
 * `AlertDialog` by hand; destructive confirms use `variant="destructive"`.
 */
export function ConfirmationDialog({
  open,
  onOpenChange,
  onConfirm,
  title,
  description,
  confirmText = "Confirmar",
  pendingText,
  cancelText = "Cancelar",
  variant = "default",
  loading = false,
  manualClose = false
}: ConfirmationDialogProps) {
  const [isConfirming, setIsConfirming] = useState(false);
  const isBusy = loading || isConfirming;
  const handleConfirm = async () => {
    if (isBusy) {
      return;
    }
    setIsConfirming(true);
    try {
      await onConfirm();
      if (!manualClose) {
        onOpenChange(false);
      }
    } catch {
      // Keep the dialog open so the caller can surface the error.
    } finally {
      setIsConfirming(false);
    }
  };
  return <AlertDialog onOpenChange={onOpenChange} open={open}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isBusy} variant="ghost">
            {cancelText}
          </AlertDialogCancel>
          <AlertDialogAction disabled={isBusy} onClick={handleConfirm} variant={variant}>
            {isBusy && pendingText ? pendingText : confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>;
}